// Row contracts and jsonb shapes of personal crews (M4-01; 02 §personal_crew … §sharing_policy,
// §Reservations; 05 §Personal crews). Same conventions as `rows.ts`: keys are column names,
// nullability is the column's, enums are the CHECK's; bound in `contract-bindings.ts`.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import {
  AutonomyMode,
  CrewActionKind,
  CrewAgentSource,
  CrewCapPeriod,
  CrewContextLayer,
  CrewContextVisibility,
  CrewMemoryBucket,
  CrewMemorySubjectKind,
  CrewProposalKind,
  CrewProposalSource,
  CrewProposalState,
  PersonalCrewPauseReason,
  PersonalCrewState,
  RunIdentityMode,
  SharingRule,
  SharingSubjectKind,
} from "./enums";
import { BoundedJson, IsoDate, LIMITS, MinorAmount, Timestamp, Uuid } from "./primitives";

const actor = ActorShortColumns.shape;
const stamps = { created_at: Timestamp, updated_at: Timestamp };

// jsonb shapes ----------------------------------------------------------------------------------

/** One interview answer as the owner gave it (verbatim; bounded). */
export const CrewOnboardingAnswer = z
  .strictObject({
    answer: z.string().max(LIMITS.longText),
    answered_at: Timestamp,
  })
  .meta({ id: "CrewOnboardingAnswer" });

/**
 * `personal_crew.onboarding_state` (agent-os.md §intake): resumable — each answer is kept as it
 * arrives; `scaffolded_at` is set when the answers were written into context layers.
 */
export const CrewOnboardingState = z
  .strictObject({
    intake_version: z.int().min(1).optional(),
    answers: z.record(z.string().max(LIMITS.key), CrewOnboardingAnswer).optional(),
    answered_q: z.array(z.string().max(LIMITS.key)).max(LIMITS.list).optional(),
    scaffolded_at: Timestamp.nullable().optional(),
    last_rerun_at: Timestamp.nullable().optional(),
  })
  .meta({ id: "CrewOnboardingState" });
export type CrewOnboardingState = z.infer<typeof CrewOnboardingState>;

/** Where a context layer came from: an interview question, an owner edit, a run. */
export const CrewSourceRef = z
  .strictObject({
    kind: z.enum(["onboarding", "owner", "run"]),
    id: z.string().max(LIMITS.key).nullable(),
  })
  .meta({ id: "CrewSourceRef" });
export type CrewSourceRef = z.infer<typeof CrewSourceRef>;

/** Proposal and reminder text bounds (M4-03). */
export const CREW_TEXT = 500;
export const CREW_DRAFT = 10_000;

/**
 * `crew_proposal.payload` (M4-03), bounded: a reply's draft, a reminder's text, a task update's
 * status and comment. The kind says which keys apply (the API writes only those).
 */
export const CrewProposalPayload = z
  .strictObject({
    body_markdown: z.string().max(CREW_DRAFT).optional(),
    text: z.string().max(CREW_TEXT).optional(),
    status: z.string().max(LIMITS.key).optional(),
    comment: z.string().max(CREW_DRAFT).optional(),
  })
  .meta({ id: "CrewProposalPayload" });
export type CrewProposalPayload = z.infer<typeof CrewProposalPayload>;

// Rows ------------------------------------------------------------------------------------------

export const PersonalCrew = z
  .object({
    company_id: Uuid,
    membership_id: Uuid,
    owner_membership_id: Uuid,
    personal_space_id: Uuid,
    brief_channel_id: Uuid,
    context_pack_ref: z.string().nullable(),
    onboarding_state: CrewOnboardingState,
    heartbeat_schedule: z.array(z.string()),
    max_concurrency: z.int().min(1).max(8),
    model_policy: BoundedJson,
    cap_minor: MinorAmount.nullable(),
    state: PersonalCrewState,
    pause_reason: PersonalCrewPauseReason.nullable(),
    brief_enabled: z.boolean(),
    brief_time: z.string(),
    brief_days: z.array(z.int().min(1).max(7)),
    last_brief_on: IsoDate.nullable(),
    triage_enabled: z.boolean(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "PersonalCrew" });
export type PersonalCrew = z.infer<typeof PersonalCrew>;

export const CrewAgent = z
  .object({
    company_id: Uuid,
    id: Uuid,
    crew_membership_id: Uuid,
    agent_profile_id: Uuid,
    name: z.string(),
    skill_pack_ids: z.array(Uuid),
    source: CrewAgentSource,
    enabled: z.boolean(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "CrewAgent" });
export type CrewAgent = z.infer<typeof CrewAgent>;

export const RoleSkillPolicy = z
  .object({
    company_id: Uuid,
    id: Uuid,
    role_id: Uuid,
    allowed_skill_pack_ids: z.array(Uuid),
    default_skill_pack_ids: z.array(Uuid),
    budget_money_cents: MinorAmount.nullable(),
    budget_period: CrewCapPeriod,
    allowed_action_kinds: z.array(CrewActionKind),
    ...actor,
    ...stamps,
  })
  .meta({ id: "RoleSkillPolicy" });
export type RoleSkillPolicy = z.infer<typeof RoleSkillPolicy>;

export const AutonomyPolicy = z
  .object({
    company_id: Uuid,
    id: Uuid,
    crew_membership_id: Uuid,
    action_kind: CrewActionKind,
    mode: AutonomyMode,
    identity_mode: RunIdentityMode,
    constraints: BoundedJson,
    ...actor,
    ...stamps,
  })
  .meta({ id: "AutonomyPolicy" });
export type AutonomyPolicy = z.infer<typeof AutonomyPolicy>;

export const SharingPolicy = z
  .object({
    company_id: Uuid,
    id: Uuid,
    crew_membership_id: Uuid,
    subject_kind: SharingSubjectKind,
    rule: SharingRule,
    ...actor,
    ...stamps,
  })
  .meta({ id: "SharingPolicy" });
export type SharingPolicy = z.infer<typeof SharingPolicy>;

export const CrewContextLayerRow = z
  .object({
    company_id: Uuid,
    id: Uuid,
    crew_membership_id: Uuid,
    layer: CrewContextLayer,
    body: z.string(),
    object_key: z.string().nullable(),
    visibility: CrewContextVisibility,
    confirmed_by_owner: z.boolean(),
    as_of: Timestamp,
    source_ref: CrewSourceRef.nullable(),
    supersedes_id: Uuid.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "CrewContextLayer" });
export type CrewContextLayerRow = z.infer<typeof CrewContextLayerRow>;

export const CrewCap = z
  .object({
    company_id: Uuid,
    per_crew_cap_minor: MinorAmount.nullable(),
    company_cap_minor: MinorAmount.nullable(),
    period_type: CrewCapPeriod,
    ...actor,
    ...stamps,
  })
  .meta({ id: "CrewCap" });
export type CrewCap = z.infer<typeof CrewCap>;

export const CrewProposalRow = z
  .object({
    company_id: Uuid,
    id: Uuid,
    crew_membership_id: Uuid,
    owner_membership_id: Uuid,
    kind: CrewProposalKind,
    state: CrewProposalState,
    inbox_item_id: Uuid.nullable(),
    task_id: Uuid.nullable(),
    thread_id: Uuid.nullable(),
    approval_id: Uuid.nullable(),
    snooze_until: Timestamp.nullable(),
    remind_at: Timestamp.nullable(),
    payload: CrewProposalPayload,
    reason: z.string(),
    source: CrewProposalSource,
    run_id: Uuid.nullable(),
    decided_at: Timestamp.nullable(),
    decided_by_membership_id: Uuid.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "CrewProposalRow" });
export type CrewProposalRow = z.infer<typeof CrewProposalRow>;

export const CrewMemoryGrant = z
  .object({
    company_id: Uuid,
    id: Uuid,
    crew_membership_id: Uuid,
    bucket: CrewMemoryBucket,
    subject_kind: CrewMemorySubjectKind,
    subject_id: Uuid,
    granted_by_membership_id: Uuid,
    revoked_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "CrewMemoryGrant" });
export type CrewMemoryGrant = z.infer<typeof CrewMemoryGrant>;
